/**
 * Materials — `docs/visit-page.md` §8 (Boards 2, 3 A; the phone's Board 16 C).
 *
 * `GET /api/v1/visits/:id/materials?view=status|day` reads the tab;
 * `POST /materials` adds a line, `PATCH /materials/:line` changes its quantity,
 * source or days. Where a line stands is derived, never typed
 * (`visit_material_state()`, §4.3) — the server sends the state and the
 * detail that goes with it; nothing here is a free-text status.
 */
import { z } from 'zod';
import { MATERIAL_SOURCES } from './values.js';
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
/** `?view=` — *By status · By day* (segmented, remembered per person, §8.1). */
export const MATERIALS_VIEWS = ['status', 'day'];
export const materialsQuerySchema = z.object({
    /** Absent: the view the person last chose (remembered per person, §8.1); given: that view, remembered. */
    view: z.enum(MATERIALS_VIEWS).optional(),
    /** *Find a material* — the lines whose names hold these words; the summary strip stays the visit's. */
    q: z.string().trim().max(200).optional(),
});
/**
 * `GET /visits/:id/materials/catalog?q=` — what **Add a material** and the swap
 * sheet's *＋ Find another item* search: the catalog's items whose names hold
 * the words typed (the first 20), each with the unit it is sold in, and the
 * vendors a line can come from.
 */
export const materialCatalogQuerySchema = z.object({
    q: z.string().trim().max(200).default(''),
});
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
const qty = z.number().positive('Add a quantity.').max(1_000_000_000);
const dayIndex = z.number().int().min(1).max(366);
/** How much of a line each day needs (`work_item_material_days`) — the *Needed* chips, *Spread over days*. */
export const materialDaysSchema = z.array(z.object({ day: dayIndex, qty })).min(1, 'Pick a day.');
/** `POST /visits/:id/materials` — **Add a material** (§5.2: item, qty, source, days). */
export const addMaterialSchema = z.object({
    itemId: z.string().uuid(),
    qty,
    source: z.enum(MATERIAL_SOURCES),
    /** Where it is ordered from, or bought (a `field` line). */
    vendorId: z.string().uuid().nullish(),
    days: materialDaysSchema,
});
/**
 * `PATCH /visits/:id/materials/:line` — ⋯ *Edit* and *Spread over days*: the
 * quantity, the source, the needed days (§5.2). The plan line is never edited
 * (§2.5).
 */
export const patchMaterialSchema = z.object({
    version: z.number().int().min(1),
    qty: qty.optional(),
    source: z.enum(MATERIAL_SOURCES).optional(),
    vendorId: z.string().uuid().nullable().optional(),
    days: materialDaysSchema.optional(),
}).refine((body) => body.qty !== undefined || body.source !== undefined
    || body.vendorId !== undefined || body.days !== undefined, { message: 'Nothing to change.' });
/** `POST /visits/:id/materials/:line/remove` — ⋯ *Remove*: voided with who, when and why (rule 2). */
export const removeMaterialSchema = z.object({
    reason: z.string().trim().min(1, 'Say why.').max(2000),
});
/** `POST /visits/:id/materials/:line/move` — ⋯ *Move to another visit* (on the same job). */
export const moveMaterialSchema = z.object({
    toVisitId: z.string().uuid(),
    reason: z.string().trim().max(2000).default(''),
});
//# sourceMappingURL=materials.js.map